import { isPrivateBeta } from '../lib/betaStorage'
import { useEffect, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { Avatar } from '../components/Avatar'
import { useApp } from '../store/AppContext'
import { localISODate } from '../lib/date'
import { useSelectedYouthParticipant } from '../lib/youthParticipants'
import {
  availableInPeriod,
  currentFundingPeriod,
  fundingDate,
  generateFundingPeriods,
  loadFunding,
  loadLeoFunding,
  loadLizzieFunding,
  money,
  nextFundingPeriod,
  periodStatus,
  planTotals,
  realignFundingToPlanDates,
  releasedAmount,
  saveFunding,
  saveLeoFunding,
  saveLizzieFunding,
  serviceBudgetFor,
  validateFundingPeriods,
} from '../lib/funding'
import type { FundingCadenceMonths, FundingCategory, FundingPeriod, FundingPlan, ServiceBudget } from '../lib/funding'

const STATUS_LABEL = { released: 'Past period', current: 'Available now', upcoming: 'Not yet released' }
const STATUS_STYLE = {
  released: 'bg-slate-100 text-slate-600',
  current: 'bg-emerald-100 text-emerald-800',
  upcoming: 'bg-amber-50 text-amber-800',
}

export function Funding({ profile = 'adult', embedded = false }: { profile?: 'adult' | 'leo'; embedded?: boolean }) {
  const { currentUser, allUsers, can } = useApp()
  const isYouth = profile === 'leo'
  const { participant } = useSelectedYouthParticipant()
  const isLizzie = isYouth && participant.id === 'u-jnr'
  const loadCurrentFunding = isLizzie ? loadLizzieFunding : isYouth ? loadLeoFunding : loadFunding
  const todayISO = localISODate()
  const [funding, setFunding] = useState(loadCurrentFunding)
  const [editingPlan, setEditingPlan] = useState(false)
  const [planError, setPlanError] = useState('')
  const canEditFunding = !isPrivateBeta && (isYouth ? currentUser.id === 'u-lillian' : can('manage_funding'))

  useEffect(() => {
    const eventName = isLizzie ? 'marnixia-lizzie-funding-changed' : isYouth ? 'marnixia-leo-funding-changed' : 'marnixia-funding-changed'
    const refresh = () => setFunding(isLizzie ? loadLizzieFunding() : isYouth ? loadLeoFunding() : loadFunding())
    refresh()
    window.addEventListener(eventName, refresh)
    return () => window.removeEventListener(eventName, refresh)
  }, [isYouth, isLizzie])

  const updateFunding = (next: typeof funding) => {
    setFunding(next)
    if (isLizzie) saveLizzieFunding(next)
    else if (isYouth) saveLeoFunding(next)
    else saveFunding(next)
  }

  const ownServiceBudget = currentUser.role === 'allied_health'
    ? serviceBudgetFor(funding.serviceBudgets, currentUser.id)
    : undefined
  const canSeeWholePlan = isYouth ? currentUser.id === 'u-lillian' : ['participant', 'rep', 'coordinator'].includes(currentUser.role)
  const totals = planTotals(funding.plan, todayISO)

  if (currentUser.role === 'allied_health') {
    return (
      <div className={embedded ? 'text-[#344453]' : 'min-h-screen bg-white pb-28 text-[#344453]'}>
        {!embedded && <TopBar title={isYouth ? `My ${participant.name} Service Budget` : "My Service Budget"} subtitle="Your allocation and funding release periods" />}
        <div className="space-y-3.5 px-3.5 py-4">
          {!ownServiceBudget && <EmptyBudget />}
          {ownServiceBudget && <ServiceBudgetCard budget={ownServiceBudget} user={currentUser} todayISO={todayISO} />}
          <div className="rounded-[20px] border border-[#d8e2ec] bg-[#f8fafc] p-4 text-xs font-semibold leading-5 text-[#5f7891]">
            Funding period dates are maintained by the participant or nominee. This view only shows this clinician's participant-approved allocation.
          </div>
        </div>
      </div>
    )
  }

  if (!canSeeWholePlan) {
    return <div className={embedded ? 'text-[#344453]' : 'min-h-screen bg-white pb-28 text-[#344453]'}>{!embedded && <TopBar title={isYouth ? `${participant.name}’s Funding` : "Funding"} />}<div className="px-3.5 py-4"><div className="rounded-[20px] border border-[#d5e1eb] bg-white p-4 text-sm font-semibold text-[#7c8997] shadow-[0_5px_18px_rgba(73,91,110,.04)]">Funding is not available to this profile.</div></div></div>
  }

  const updatePlanDates = (changes: Partial<Pick<FundingPlan, 'startDate' | 'endDate'>>) => {
    const startDate = changes.startDate ?? funding.plan.startDate
    const endDate = changes.endDate ?? funding.plan.endDate
    if (!startDate || !endDate || startDate > endDate) {
      setPlanError('Plan finish date must be on or after the plan start date.')
      return
    }
    setPlanError('')
    updateFunding(realignFundingToPlanDates(funding, startDate, endDate))
  }

  const updateCategory = (categoryId: string, next: FundingCategory) => {
    updateFunding({
      ...funding,
      plan: {
        ...funding.plan,
        categories: funding.plan.categories.map((item) => item.id === categoryId ? next : item),
      },
    })
  }

  const updateServiceBudget = (userId: string, next: ServiceBudget) => {
    updateFunding({ ...funding, serviceBudgets: funding.serviceBudgets.map((item) => item.userId === userId ? next : item) })
  }

  return (
    <div className={embedded ? 'text-[#344453]' : 'min-h-screen bg-white pb-28 text-[#344453]'}>
      {!embedded && <TopBar title={isYouth ? `${participant.name}’s Funding & Budgets` : "Funding & Budget"} subtitle="NDIS funding periods" />}
      <main className={embedded ? 'space-y-3.5' : 'space-y-3.5 px-3.5 py-4'}>
        <section className="rounded-[24px] border border-[#cfdde9] bg-white p-4 shadow-[0_8px_24px_rgba(73,91,110,.05)]">
          <div className="flex items-start justify-between gap-4">
            <div><p className="text-[11px] font-extrabold uppercase tracking-[.1em] text-[#7897b5]">{isYouth ? `${participant.name}’s whole NDIS plan` : "Whole NDIS plan"}</p><p className="mt-1.5 text-[28px] font-black tracking-[-.025em] text-[#27384a]">{money(totals.allocated)}</p></div>
            {canEditFunding && <button onClick={() => setEditingPlan((value) => !value)} className="min-h-10 rounded-[13px] border border-[#d8e2ec] bg-[#f8fafc] px-3 py-2 text-[11px] font-extrabold text-[#6f8da8] transition active:scale-[.99]">{editingPlan ? 'Done' : 'Edit plan dates'}</button>}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <Metric label="Released to date" value={money(totals.released)} />
            <Metric label="Available now" value={money(totals.availableNow)} />
            <Metric label="Spent" value={money(totals.spent)} />
            <Metric label="Plan remaining" value={money(totals.remaining)} />
          </div>
          {editingPlan && canEditFunding ? (
            <div className="mt-4 rounded-[18px] border border-[#d8e2ec] bg-[#f8fafc] p-3.5">
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Plan start" value={funding.plan.startDate} onChange={(value) => updatePlanDates({ startDate: value })} />
                <DateField label="Plan finish" value={funding.plan.endDate} onChange={(value) => updatePlanDates({ endDate: value })} />
              </div>
              {planError && <p className="mt-2 rounded-[13px] border border-rose-100 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">{planError}</p>}
              <p className="mt-2 text-[10px] font-semibold leading-4 text-[#7c8997]">Changing plan dates automatically realigns 3, 6 and 12 month schedules. Custom schedules are left unchanged.</p>
            </div>
          ) : (
            <p className="mt-3 text-xs font-semibold text-[#7c8997]">{fundingDate(funding.plan.startDate)} → {fundingDate(funding.plan.endDate)}</p>
          )}
        </section>

        <section className="rounded-[22px] border border-[#d8e2ec] bg-white p-4 shadow-[0_5px_18px_rgba(73,91,110,.04)]">
          <p className="text-[11px] font-extrabold uppercase tracking-[.1em] text-[#7897b5]">Plan setup</p><h2 className="mt-1 text-[17px] font-black text-[#344453]">Flexible funding periods</h2>
          <p className="mt-1.5 text-[12px] font-semibold leading-5 text-[#7c8997]">Choose 3, 6 or 12 months for quick setup, or Custom when the NDIA gives different dates. Every individual release period can then be edited separately.</p>
        </section>

        <section>
          <div className="mb-2 px-1"><p className="text-[11px] font-extrabold uppercase tracking-[.1em] text-[#7897b5]">Plan breakdown</p><h2 className="mt-1 text-[17px] font-black text-[#344453]">Funding components</h2><p className="mt-0.5 text-[12px] font-semibold text-[#7c8997]">Each component can have its own release timeline.</p></div>
          <div className="flex flex-col gap-3">
            {funding.plan.categories.map((category) => (
              <FundingComponentCard key={category.id} category={category} plan={funding.plan} todayISO={todayISO} editable={canEditFunding} onChange={(next) => updateCategory(category.id, next)} />
            ))}
          </div>
        </section>

        <section>
          <div className="mb-2 px-1"><p className="text-[11px] font-extrabold uppercase tracking-[.1em] text-[#7897b5]">Service allocations</p><h2 className="mt-1 text-[17px] font-black text-[#344453]">Allied health budgets</h2><p className="mt-0.5 text-[12px] font-semibold text-[#7c8997]">Discipline allocations can also use editable funding periods.</p></div>
          <div className="flex flex-col gap-3">
            {funding.serviceBudgets.map((budget) => (
              <ServiceBudgetCard key={budget.userId} budget={budget} user={allUsers.find((user) => user.id === budget.userId)} todayISO={todayISO} editable={canEditFunding} plan={funding.plan} onChange={(next) => updateServiceBudget(budget.userId, next)} />
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}

function EmptyBudget() {
  return <div className="rounded-[22px] border border-dashed border-[#cfdde9] bg-white p-5 text-sm font-semibold text-[#7c8997]">No service budget has been assigned to this allied health profile yet.</div>
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-[16px] border border-[#e2e9f0] bg-[#f8fafc] p-3"><p className="text-[11px] font-semibold text-[#7c8997]">{label}</p><p className="mt-1 text-[17px] font-black text-[#344453]">{value}</p></div>
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="text-[11px] font-bold text-[#647484]"><span className="mb-1 block">{label}</span><input type="date" value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-[13px] border border-[#d8e2ec] bg-white px-2.5 py-2.5 text-xs font-semibold text-[#344453] outline-none focus:border-[#8da8c1] focus:ring-2 focus:ring-[#e8f0f7]" /></label>
}

function CadenceEditor({ cadence, onSelect }: { cadence: FundingCadenceMonths; onSelect: (value: FundingCadenceMonths) => void }) {
  const options: { value: FundingCadenceMonths; label: string }[] = [
    { value: 3, label: '3 months' }, { value: 6, label: '6 months' }, { value: 12, label: '12 months' }, { value: 'custom', label: 'Custom' },
  ]
  return <div className="grid grid-cols-4 gap-1.5">{options.map((option) => <button key={String(option.value)} type="button" onClick={() => onSelect(option.value)} className={`rounded-xl border px-2 py-2 text-[10px] font-bold ${cadence === option.value ? 'border-[#8da8c1] bg-[#eef4f8] text-[#55799b]' : 'border-[#d8e2ec] bg-white text-[#6f7f8f]'}`}>{option.label}</button>)}</div>
}

function FundingComponentCard({ category, plan, todayISO, editable, onChange }: { category: FundingCategory; plan: FundingPlan; todayISO: string; editable: boolean; onChange: (next: FundingCategory) => void }) {
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState('')
  const current = currentFundingPeriod(category.periods, todayISO)
  const next = nextFundingPeriod(category.periods, todayISO)
  const released = releasedAmount(category.periods, todayISO)
  const planRemaining = Math.max(0, category.allocated - category.spent)
  const currentAvailable = current ? availableInPeriod(current) : 0

  const changeCadence = (cadence: FundingCadenceMonths) => {
    setEditError('')
    if (cadence === 'custom') return onChange({ ...category, cadenceMonths: cadence })
    onChange({ ...category, cadenceMonths: cadence, periods: generateFundingPeriods(category.id, plan.startDate, plan.endDate, category.allocated, cadence, category.periods) })
  }

  const changePeriod = (periodId: string, changes: Partial<FundingPeriod>) => {
    const periods = category.periods.map((period) => period.id === periodId ? { ...period, ...changes } : period)
    const error = validateFundingPeriods(periods)
    if (error) {
      setEditError(error)
      return
    }
    setEditError('')
    onChange({ ...category, cadenceMonths: 'custom', periods })
  }

  return (
    <article className="rounded-[22px] border border-[#cfdde9] bg-white p-4 shadow-[0_5px_18px_rgba(73,91,110,.04)]">
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-[15px] font-black text-[#344453]">{category.label}</p><p className="mt-0.5 text-[11px] font-semibold text-[#7c8997]">{money(category.spent)} spent · {money(planRemaining)} plan remaining</p></div>
        <div className="text-right"><p className="text-[15px] font-black text-[#5f82a4]">{money(category.allocated)}</p>{editable && <button onClick={() => { setEditing((value) => !value); setEditError('') }} className="mt-1 text-[11px] font-extrabold text-[#6889a8]">{editing ? 'Done' : 'Edit timeline'}</button>}</div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs"><div className="rounded-[16px] border border-[#e4ebf1] bg-[#f8fafc] p-3"><p className="font-semibold text-[#7c8997]">Released to date</p><p className="mt-1 font-black text-[#344453]">{money(released)}</p></div><div className="rounded-[16px] border border-[#d9e7e3] bg-[#f7fbf9] p-3"><p className="font-semibold text-[#5b8c82]">Available this period</p><p className="mt-1 font-black text-[#416f68]">{money(currentAvailable)}</p></div></div>
      {editing && editable && <div className="mt-4 rounded-[18px] border border-[#d8e2ec] bg-[#f8fafc] p-3.5"><p className="mb-2 text-[11px] font-bold text-slate-700">Default release timeline</p><CadenceEditor cadence={category.cadenceMonths} onSelect={changeCadence} /><p className="mt-2 text-[10px] leading-4 text-slate-500">Changing 3/6/12 months regenerates the timeline from the plan dates. Editing any individual period switches this component to Custom.</p>{editError && <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">{editError}</p>}</div>}
      {current && <div className="mt-3 rounded-[16px] border border-[#d9e7e3] bg-[#f7fbf9] p-3"><p className="text-xs font-bold text-emerald-900">Current funding period</p><p className="mt-0.5 text-[11px] text-emerald-800">{fundingDate(current.startDate)} → {fundingDate(current.endDate)}</p></div>}
      {next && <p className="mt-3 text-[11px] font-semibold text-slate-500">Next release: {fundingDate(next.startDate)} · {money(next.amount)}</p>}
      <div className="mt-3 border-t border-slate-100 pt-3"><p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Funding periods</p><div className="flex flex-col gap-2">{category.periods.map((period) => <PeriodRow key={period.id} period={period} todayISO={todayISO} editable={editing && editable} onChange={(changes) => changePeriod(period.id, changes)} />)}</div></div>
    </article>
  )
}

function PeriodRow({ period, todayISO, editable, onChange }: { period: FundingPeriod; todayISO: string; editable?: boolean; onChange?: (changes: Partial<FundingPeriod>) => void }) {
  const status = periodStatus(period, todayISO)
  if (editable && onChange) {
    return <div className="rounded-[16px] border border-[#d8e2ec] bg-[#f8fafc] p-3"><div className="grid grid-cols-2 gap-2"><DateField label="Period start" value={period.startDate} onChange={(value) => onChange({ startDate: value })} /><DateField label="Period finish" value={period.endDate} onChange={(value) => onChange({ endDate: value })} /></div><div className="mt-2 grid grid-cols-3 gap-2"><NumberField label="Released $" value={period.amount} onChange={(value) => onChange({ amount: value })} /><NumberField label="Rollover $" value={period.rollover} onChange={(value) => onChange({ rollover: value })} /><NumberField label="Spent $" value={period.spent} onChange={(value) => onChange({ spent: value })} /></div></div>
  }
  return <div className="flex items-center justify-between gap-3 rounded-[16px] border border-[#e2e9f0] bg-[#f8fafc] px-3 py-2.5"><div><p className="text-[11px] font-bold text-slate-700">{fundingDate(period.startDate)} → {fundingDate(period.endDate)}</p><p className="mt-0.5 text-[10px] text-slate-500">{money(period.amount)}{period.rollover > 0 ? ` · ${money(period.rollover)} rollover` : ''}</p></div><span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${STATUS_STYLE[status]}`}>{STATUS_LABEL[status]}</span></div>
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return <label className="text-[10px] font-bold text-slate-600"><span className="mb-1 block">{label}</span><input type="number" min="0" step="0.01" value={value} onChange={(event) => onChange(Number(event.target.value) || 0)} className="w-full rounded-[13px] border border-[#d8e2ec] bg-white px-2 py-2.5 text-xs font-semibold text-[#344453] outline-none focus:border-[#8da8c1] focus:ring-2 focus:ring-[#e8f0f7]" /></label>
}

function ServiceBudgetCard({ budget, user, todayISO, editable = false, plan, onChange }: { budget: ServiceBudget; user?: ReturnType<typeof useApp>['currentUser']; todayISO: string; editable?: boolean; plan?: FundingPlan; onChange?: (next: ServiceBudget) => void }) {
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState('')
  const remaining = Math.max(0, budget.allocated - budget.spent)
  const current = currentFundingPeriod(budget.periods, todayISO)
  const next = nextFundingPeriod(budget.periods, todayISO)
  const released = releasedAmount(budget.periods, todayISO)
  const availableNow = Math.max(0, released - budget.spent)
  const sessions = budget.estimatedSessionCost > 0 ? Math.floor(availableNow / budget.estimatedSessionCost) : 0

  const changeCadence = (cadence: FundingCadenceMonths) => {
    if (!onChange || !plan) return
    setEditError('')
    if (cadence === 'custom') return onChange({ ...budget, cadenceMonths: cadence })
    onChange({ ...budget, cadenceMonths: cadence, periods: generateFundingPeriods(`svc-${budget.userId}`, plan.startDate, plan.endDate, budget.allocated, cadence, budget.periods) })
  }

  const changePeriod = (periodId: string, changes: Partial<FundingPeriod>) => {
    if (!onChange) return
    const periods = budget.periods.map((period) => period.id === periodId ? { ...period, ...changes } : period)
    const error = validateFundingPeriods(periods)
    if (error) {
      setEditError(error)
      return
    }
    setEditError('')
    onChange({ ...budget, cadenceMonths: 'custom', periods })
  }

  return <div className="rounded-[22px] border border-[#cfdde9] bg-white p-4 shadow-[0_5px_18px_rgba(73,91,110,.04)]"><div className="flex items-center gap-3">{user && <Avatar user={user} size={42} />}<div className="min-w-0 flex-1"><p className="text-[15px] font-black text-[#344453]">{budget.discipline}</p>{user && <p className="text-[11px] font-semibold text-[#7c8997]">{user.name}</p>}</div><div className="text-right"><p className="text-[15px] font-black text-[#5f82a4]">{money(budget.allocated)}</p>{editable && onChange && <button onClick={() => { setEditing((value) => !value); setEditError('') }} className="mt-1 text-[11px] font-extrabold text-[#6889a8]">{editing ? 'Done' : 'Edit periods'}</button>}</div></div><div className="mt-3 grid grid-cols-2 gap-2 text-xs"><div className="rounded-[16px] border border-[#e4ebf1] bg-[#f8fafc] p-3"><p className="text-slate-500">Plan remaining</p><p className="mt-1 font-extrabold text-slate-900">{money(remaining)}</p></div><div className="rounded-[16px] border border-[#d9e7e3] bg-[#f7fbf9] p-3"><p className="text-emerald-700">Available now</p><p className="mt-1 font-extrabold text-emerald-900">{money(availableNow)}</p></div></div>{editing && editable && onChange && plan && <div className="mt-3 rounded-[18px] border border-[#d8e2ec] bg-[#f8fafc] p-3.5"><CadenceEditor cadence={budget.cadenceMonths} onSelect={changeCadence} />{editError && <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">{editError}</p>}<div className="mt-3 flex flex-col gap-2">{budget.periods.map((period) => <PeriodRow key={period.id} period={period} todayISO={todayISO} editable onChange={(changes) => changePeriod(period.id, changes)} />)}</div></div>}{current && !editing && <div className="mt-3 rounded-[16px] border border-[#e2e9f0] bg-[#f8fafc] p-3 text-[11px] font-semibold text-[#647484]"><p className="font-bold text-slate-800">Current release · {fundingDate(current.startDate)} → {fundingDate(current.endDate)}</p><p className="mt-1">{money(current.amount)} released · {money(current.spent)} recorded spend{current.rollover > 0 ? ` · ${money(current.rollover)} rollover` : ''}</p></div>}<p className="mt-3 text-[11px] font-semibold text-[#7c8997]">Est. {money(budget.estimatedSessionCost)} per session · {budget.frequency} · about {sessions} sessions from funding currently available</p>{next && <p className="mt-1.5 text-[11px] font-semibold text-slate-500">Next release {fundingDate(next.startDate)} · {money(next.amount)}</p>}</div>
}

